import React, { useEffect, useState, Suspense, lazy } from "react";
import { Route, Switch, Redirect } from "react-router-dom";
import PrivateRoute from "./PrivateRoute";
import RoutesCustom from "./RoutesCustom";
import i18n from "./translation/i18n";
import {
  LEGACY_GUIDE_PRODUCT_REDIRECTS,
  resolveLegacyGuideProductRedirect
} from "./shared/legacyGuideProductRedirects";
import { ALL_PRODUCT_ROUTES } from "./redesign/content";

/* Code-split theo route: giảm JS parse/execute trên main thread (TBT), đặc biệt mobile */
const QrGenerator = lazy(() => import("./components/QrGenerator/QrGenerator"));
const NewDetail = lazy(() => import("./components/News/NewDetail/NewDetail"));
const NewsList = lazy(() => import("./components/News/NewsList/NewsList"));
const UseCaseList = lazy(() => import("./components/UseCase/UseCaseList"));
const UseCaseDetail = lazy(() => import("./components/UseCase/UseCaseDetail"));
const DocumentList = lazy(() => import("./components/Document/DocumentList"));
const DocumentDetail = lazy(() => import("./components/Document/DocumentDetail"));
const NotFound = lazy(() => import("./NotFound"));
const Dashboard = lazy(() => import("./legacy-pages/Dashboard/Dashboard"));
const ResetPassword = lazy(() => import("./ResetPassword"));
const IntroductionContainer = lazy(() =>
  import("./components/Container/IntructionContainer/IntroductionContainer")
);
const PrivacyContainer = lazy(() => import("./components/Container/PrivacyContainer/PrivacyContainer"));
const ContactContainer = lazy(() => import("./components/Container/ContactContainer/ContactContainer"));
const TOUContainer = lazy(() => import("./components/Container/TOUContainer/TOUContainer"));
const ProductPage = lazy(() => import("./redesign/ProductPage"));
const ServicePackRedesign = lazy(() => import("./redesign/ServicePackRedesign"));
const HelpRedesignPage = lazy(() => import("./redesign/HelpRedesignPage"));
const ScanQrCodePage = lazy(() => import("./redesign/ScanQrCodePage"));

// Chỉ đọc language từ worker-injected env.
const defaultLanguage =
  (typeof window !== "undefined" && window.QRCODE_ENV?.DEFAULT_LANGUAGE) || "ja";
export const listLanguage = [defaultLanguage]

export default ({ onMount, onRerender, navbar }) => {
  const [LangList, setLangList] = useState([]);

  // Luôn đồng bộ ngôn ngữ theo env đã resolve từ worker.
  useEffect(() => {
    i18n.changeLanguage(defaultLanguage);
  }, [defaultLanguage]);

  // Hàm lấy tên đầy đủ của ngôn ngữ từ mã code
  const getLanguageName = (langCode) => {
    const languageNames = {
      'ja': 'Japanese',
      'en': 'English',
      'vi': 'Tiếng Việt',
      'fr': 'Français',
      'de': 'Deutsch',
      'zh': '中文',
      'ko': '한국어'
    };

    return languageNames[langCode] || langCode;
  };

  useEffect(() => {
    // Tạo mảng đối tượng ngôn ngữ từ listLanguage
    const formattedLanguages = listLanguage.map(key => {
      return {
        key: key,
        name: getLanguageName(key)
      }
    });

    setLangList(formattedLanguages);
  }, []);

  return (
    <Suspense
      fallback={
        <div
          className="route-loading-placeholder"
          aria-hidden="true"
        />
      }
    >
    <Switch>
      <Route
        path="/blog/:id"
        exact
        render={(props) => <NewDetail {...props} onMount={onMount} />}
      />
      <RoutesCustom
        path="/blog"
        exact
        render={() => <NewsList onMount={onMount} />}
      />

      <Route
        path="/use-cases/:id"
        exact
        render={(props) => <UseCaseDetail {...props} onMount={onMount} />}
      />
      <RoutesCustom
        path="/use-cases"
        exact
        render={() => <UseCaseList onMount={onMount} />}
      />
      <Route
        path="/document/:id"
        exact
        render={(props) => <DocumentDetail {...props} onMount={onMount} />}
      />
      <RoutesCustom
        path="/document"
        exact
        render={() => <DocumentList onMount={onMount} />}
      />

      {Object.keys(LEGACY_GUIDE_PRODUCT_REDIRECTS).map((legacyPath) => (
        <Route
          key={legacyPath}
          path={legacyPath}
          exact
          render={({ location }) => (
            <Redirect
              to={{
                pathname: resolveLegacyGuideProductRedirect(legacyPath),
                search: location.search,
                hash: location.hash
              }}
            />
          )}
        />
      ))}

      <RoutesCustom
        path="/what-is-qr-code"
        exact
        render={() => <IntroductionContainer data={LangList} lang={defaultLanguage} onMount={onMount} />}
      />
      <RoutesCustom
        path="/privacy-policy"
        exact
        render={() => <PrivacyContainer data={LangList} lang={defaultLanguage} onMount={onMount} />}
      />
      <RoutesCustom
        path="/contact"
        exact
        render={() => <ContactContainer data={LangList} lang={defaultLanguage} onMount={onMount} />}
      />
      <RoutesCustom
        path="/term-of-use"
        exact
        render={() => <TOUContainer data={LangList} lang={defaultLanguage} onMount={onMount} />}
      />
      <RoutesCustom
        path="/service-pack"
        exact
        render={() => <ServicePackRedesign onMount={onMount} />}
      />
      {ALL_PRODUCT_ROUTES.map((product) => (
        <RoutesCustom
          key={product.key}
          path={product.path}
          exact
          render={() => (
            <ProductPage productKey={product.key} onMount={onMount} />
          )}
        />
      ))}

      <RoutesCustom
        path="/reset-password"
        exact
        render={() => <ResetPassword onMount={onMount} navbar={navbar} />}
      />
      <RoutesCustom
        path="/chinh-sach-bao-mat"
        exact
        render={() => {
          if (i18n.language !== 'vi') {
            return <Redirect to="/privacy-policy" />;
          }
          return <PrivacyContainer data={LangList} lang={defaultLanguage} onMount={onMount} />;
        }}
      />
      <RoutesCustom
        path="/lien-he"
        exact
        render={() => <Redirect to="/contact" />}
      />
      <RoutesCustom
        path="/dieu-khoan-su-dung"
        exact
        render={() => {
          if (i18n.language !== 'vi') {
            return <Redirect to="/term-of-use" />;
          }
          return <TOUContainer data={LangList} lang={defaultLanguage} onMount={onMount} />;
        }}
      />
      <RoutesCustom
        path="/goi-dich-vu"
        exact
        render={() => <Redirect to="/service-pack" />}
      />
      <RoutesCustom
        path="/qr-code-la-gi"
        exact
        render={() => <Redirect to="/what-is-qr-code" />}
      />
      <Route
        path="/help/:tab?"
        render={() => <HelpRedesignPage language={defaultLanguage} onMount={onMount} />}
      />
      <RoutesCustom
        path="/scan-qr-code"
        exact
        render={() => <ScanQrCodePage language={defaultLanguage} onMount={onMount} />}
      />
      <PrivateRoute path="/dashboard/:tab?/:subTab?" component={Dashboard} />
      {/* fallback to home if exact match fail on guides but path is base */}
      <RoutesCustom
        path="/"
        exact
        render={() => (
          <QrGenerator
            showHomeContent
            onMount={onMount}
            onRerenderPage={onRerender}
          />
        )}
      />
      <RoutesCustom
        path="/qrcode"
        exact
        component={() => (
          <Redirect to="/" />
        )}
      />
      <Route render={(props) => <NotFound {...props} />} />
    </Switch>
    </Suspense>
  )
};
